import { z } from 'zod';

// ----------------------------------------------------------------------------
// Cross-cutting primitives
// ----------------------------------------------------------------------------

// All IDs are ULIDs (Crockford base32, 26 chars). Generated with `ulidx`.
export const IdSchema = z
  .string()
  .regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'Must be a valid ULID');
export type Id = z.infer<typeof IdSchema>;

// Subdomain: lowercase, hyphen-separated, 2-63 chars (DNS label rules).
export const SubdomainSchema = z
  .string()
  .min(2)
  .max(63)
  .regex(
    /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/,
    'Must be lowercase letters, digits, and hyphens (not starting/ending with a hyphen)',
  )
  .refine((s) => !RESERVED_SUBDOMAINS.has(s), { message: 'Reserved subdomain' });
export type Subdomain = z.infer<typeof SubdomainSchema>;

export const RESERVED_SUBDOMAINS = new Set([
  'admin', 'api', 'www', 'app', 'mail', 'static',
  'cdn', 'docs', 'blog', 'help', 'support', 'status',
]);

export const EmailSchema = z.string().email().toLowerCase().trim();
export const TimestampSchema = z.string().datetime();
export const MoneySchema = z.number().int().nonnegative(); // cents

// ----------------------------------------------------------------------------
// Pagination
// ----------------------------------------------------------------------------

export const PaginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});
export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;

export const PaginatedResponseSchema = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
  });

// ----------------------------------------------------------------------------
// API envelope
// ----------------------------------------------------------------------------

export const ApiErrorSchema = z.object({
  error: z.string(),
  message: z.string().optional(),
  details: z.unknown().optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;
